import {
  ReactNode,
  useContext,
  ReactElement,
  createContext,
  useState,
} from 'react';

import ApiService from 'services/api';
import AuthContext from './AuthContext';
import {
  ICallDetails,
  ICalls,
  ICallStats,
  IReportCallStatsParams,
} from 'interfaces/Reporting';
import { IReportCallParams } from 'interfaces/Reporting';
import { DateRange } from 'components/DateRangeFilter/Dropdown';
import { logger, ErrorType } from '../utils/logger';

const INITIAL_CALL_STATS: ICallStats = {
  num_call: 0,
  avg_call_duration: 0,
  num_message: 0,
  num_complete: 0,
  sentiment: { neg: 0, neu: 0, pos: 0, compound: 0 },
};

const INITIAL_CALLS: ICalls = {
  items: [],
  total: 0,
  page: 0,
  size: 0,
  pages: 0,
};

const INITIAL_CALL_DETAILS: ICallDetails = {
  id: '',
  agent_id: 0,
  participant: {
    type: '',
    phone_number: '',
    direction: '',
    call_sid: '',
  },
  messages: [],
  events: {
    call_started: '',
    call_ended: '',
  },
  summary: {
    action: '',
    discussion: '',
    summary: '',
  },
  audio_recording_filepath: '',
  start_time: '',
  duration: 0,
  resolution: '',
  call_sid: '',
  sentiment: { neg: 0, neu: 0, pos: 0, compound: 0 },
  user_data: {},
};

interface IReportContext {
  actions: {
    getCallStats: (params: IReportCallStatsParams) => void;
    getCalls: (params: IReportCallParams) => Promise<ICalls>;
    getCallDetails: (conversation_id: string) => void;
    setDateRange: (dateRange: DateRange) => void;
    setCalls: (calls: ICalls) => void;
  };
  data: {
    callStats: ICallStats;
    calls: ICalls;
    callDetails: ICallDetails;
    dateRange: DateRange | undefined;
    isLoadingCalls: boolean;
    isLoadingCallStats: boolean;
    isLoadingCallDetails: boolean;
  };
}

interface IReportProvider {
  children: ReactNode;
}

const ReportContext = createContext<IReportContext>({
  actions: {
    getCallStats: async () => '',
    getCalls: async () => {
      return INITIAL_CALLS;
    },
    getCallDetails: async () => '',
    setDateRange: async () => '',
    setCalls: async () => '',
  },
  data: {
    callStats: INITIAL_CALL_STATS,
    calls: INITIAL_CALLS,
    callDetails: INITIAL_CALL_DETAILS,
    dateRange: undefined,
    isLoadingCalls: true,
    isLoadingCallStats: true,
    isLoadingCallDetails: true,
  },
});

export function ReportProvider(props: IReportProvider): ReactElement {
  const { children } = props;
  const {
    data: { authToken },
    actions: { isLoggedIn },
  } = useContext(AuthContext);

  const [callStats, setCallStats] = useState<ICallStats>(INITIAL_CALL_STATS);
  const [calls, setCalls] = useState<ICalls>(INITIAL_CALLS);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [callDetails, setCallDetails] =
    useState<ICallDetails>(INITIAL_CALL_DETAILS);
  const [isLoadingCalls, setIsLoadingCalls] = useState(true);
  const [isLoadingCallStats, setIsLoadingCallStats] = useState(true);
  const [isLoadingCallDetails, setIsLoadingCallDetails] = useState(true);

  const getCallStats = async (params: IReportCallStatsParams) => {
    if (!isLoggedIn()) {
      throw new Error('You are not logged in. Please log in and try again');
    }
    try {
      setIsLoadingCallStats(true);
      const token = authToken;
      const response = await ApiService.get(`reports/stats`, {
        params,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token?.access_token}`,
        },
      });

      setCallStats(response.data);
    } catch (e: any) {
      logger.error('Failed to load call statistics', e as Error, {
        errorType: ErrorType.API_ERROR,
        component: 'getCallStats',
        metadata: { params }
      });
      throw e;
    } finally {
      setIsLoadingCallStats(false);
    }
  };

  const getCalls = async (params: IReportCallParams) => {
    if (!isLoggedIn()) {
      throw new Error('You are not logged in. Please log in and try again');
    }
    try {
      setIsLoadingCalls(true);
      const token = authToken;
      const response = await ApiService.get('reports', {
        params,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token?.access_token}`,
        },
      });

      setCalls(response.data);
      return response.data;
    } catch (e: any) {
      logger.error('Failed to load call reports', e as Error, {
        errorType: ErrorType.API_ERROR,
        component: 'getCalls',
        metadata: { params }
      });
      throw e;
    } finally {
      setIsLoadingCalls(false);
    }
  };

  const getCallDetails = async (conversation_id: string) => {
    if (!isLoggedIn()) {
      throw new Error('You are not logged in. Please log in and try again');
    }
    try {
      setIsLoadingCallDetails(true);
      const token = authToken;
      const response = await ApiService.get(
        `reports/details/${conversation_id}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token?.access_token}`,
          },
        }
      );

      setCallDetails(response.data);
    } catch (e: any) {
      logger.error('Failed to load call details', e as Error, {
        errorType: ErrorType.API_ERROR,
        component: 'getCallDetails',
        metadata: { conversation_id }
      });
      throw e;
    } finally {
      setIsLoadingCallDetails(false);
    }
  };

  return (
    <ReportContext.Provider
      value={{
        data: {
          callStats,
          calls,
          callDetails,
          dateRange,
          isLoadingCalls,
          isLoadingCallStats,
          isLoadingCallDetails,
        },
        actions: {
          getCallStats,
          getCallDetails,
          getCalls,
          setDateRange,
          setCalls,
        },
      }}
    >
      {children}
    </ReportContext.Provider>
  );
}

export default ReportContext;
